import { useIsInsideMobileWebview } from "@circle-react/hooks/useIsInsideMobileWebview";
import { useMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useLocalStorageState } from "@circle-react/hooks/utils/useLocalStorageState";
import { useCallback, useEffect } from "react";

import type { Appearance, StoredAppearancePreference } from "../themes";
import {
  APPEARANCE,
  APPEARANCE_SYSTEM,
  isAppearance,
  isStoredPreference,
} from "../themes";

const PREFERS_DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export interface UseLocalStorageAppearanceProps {
  defaultAppearance?: Appearance;
}

export interface UseLocalStorageAppearanceReturn {
  clearAppearance: () => void;
  currentAppearance?: Appearance | "";
  isDarkAppearance: boolean;
  isLightAppearance: boolean;
  setAppearance: (value: Exclude<StoredAppearancePreference, "">) => void;
  /** The raw value persisted in localStorage ("light" | "dark" | "system" | ""). */
  storedPreference: StoredAppearancePreference;
  toggleAppearance: () => void;
}

export const useLocalStorageAppearance = ({
  defaultAppearance = APPEARANCE.LIGHT,
}: UseLocalStorageAppearanceProps): UseLocalStorageAppearanceReturn => {
  const [storedValue, setStoredPreference] =
    useLocalStorageState<StoredAppearancePreference>("current_appearance", "");
  const storedPreference: StoredAppearancePreference = storedValue ?? "";
  const isInsideMobileWebview = useIsInsideMobileWebview();

  const isDarkSystemAppearance = useMediaQuery(PREFERS_DARK_MEDIA_QUERY);
  const systemAppearance: Appearance = isDarkSystemAppearance
    ? APPEARANCE.DARK
    : APPEARANCE.LIGHT;

  const setAppearance = useCallback(
    (value: Exclude<StoredAppearancePreference, "">) => {
      if (isStoredPreference(value)) {
        setStoredPreference(value);
      }
    },
    [setStoredPreference],
  );

  const clearAppearance = useCallback(() => {
    setStoredPreference("");
  }, [setStoredPreference]);

  useEffect(() => {
    if (window?.webview && !window.webview.setAppearance) {
      window.webview.setAppearance = setAppearance;
    }
  }, [setAppearance]);

  const toggleAppearance = () => {
    // Resolve the currently-visible appearance, then flip it.
    // This always stores an explicit "light" or "dark" (overrides "system" / "").
    const resolved = resolveAppearance();
    setStoredPreference(
      resolved === APPEARANCE.LIGHT ? APPEARANCE.DARK : APPEARANCE.LIGHT,
    );
  };

  /**
   * Resolve the final appearance from the stored preference.
   *
   * Priority:
   *  1. Inside the native mobile webview → device prefers-color-scheme,
   *     always, regardless of any stored preference
   *  2. Explicit "light" / "dark" → use directly
   *  3. "system" → device prefers-color-scheme
   *  4. "" (no pref) → community default, falling back to device
   */
  const resolveAppearance = (): Appearance => {
    if (isInsideMobileWebview) {
      return systemAppearance;
    }
    if (isAppearance(storedPreference)) {
      return storedPreference;
    }
    if (storedPreference === APPEARANCE_SYSTEM) {
      return systemAppearance;
    }
    // No user preference – use community default; if absent, device preference.
    return isAppearance(defaultAppearance)
      ? defaultAppearance
      : systemAppearance;
  };

  const resolved = resolveAppearance();
  const isDarkAppearance = resolved === APPEARANCE.DARK;
  const isLightAppearance = resolved === APPEARANCE.LIGHT;

  // Backwards-compat: `currentAppearance` keeps the same shape callers expect.
  // When stored pref is "" we pass "" so useThemeLocalStorage can still fall
  // back to default_appearance via getIsAppearance(). For "system", or inside
  // the native mobile webview (which must win over even an explicit stored
  // preference -- see resolveAppearance above), we pass the resolved device
  // value so the rest of the pipeline sees a concrete appearance.
  const currentAppearance: Appearance | "" =
    isInsideMobileWebview || storedPreference === APPEARANCE_SYSTEM
      ? resolved
      : storedPreference;

  return {
    currentAppearance,
    setAppearance,
    clearAppearance,
    storedPreference,
    toggleAppearance,
    isDarkAppearance,
    isLightAppearance,
  };
};
